/**
 * Entrada do sistema.
 *
 * Sem sessão, mostra o login; com sessão, pergunta ao backend quem é a pessoa e
 * roteia. O Firebase avisa quando a sessão muda (inclusive ao restaurar a
 * salva), e é esse aviso que decide o que aparece — em vez de a página
 * perguntar "estou logado?" em cada lugar.
 *
 * A navegação usa o endereço (`#/desk`). Isso faz o botão Voltar do navegador
 * funcionar e permite mandar um link direto para uma tela — coisas que o
 * sistema atual não tem, porque lá tudo acontece na mesma página.
 */

import { onAuthStateChanged, signInWithPopup, signOut, type User } from 'firebase/auth';

import './estilo.css';
import { auth, provedorGoogle } from './firebase.js';
import { api, ErroApi } from './api.js';
import { escapar } from './comum/formato.js';
import { modulo, modulosDe } from './modulos.js';
import { montarHub } from './telas/hub.js';
import { montarDesk } from './telas/desk.js';
import { montarEntrada } from './telas/entrada.js';
import { montarMotorista } from './telas/motorista.js';
import { montarPedidos } from './telas/pedidos/index.js';
import { montarMaster } from './telas/master/index.js';
import { montarSst } from './telas/sst.js';
import { instalarCapturaDeErros, moduloEmUso } from './comum/erros.js';
import { instalarSocorroDeFotos } from './comum/foto.js';
import { instalarFotoGrande } from './comum/foto-grande.js';
import { registrarServiceWorker } from './comum/push.js';
import { instalarOrdenacaoDeTabelas } from './comum/ordenar-tabela.js';
import { instalarConvitePwa } from './comum/instalar.js';
import { instalarSino } from './comum/sino.js';
import { instalarDetectorDeVersao } from './comum/atualizacao.js';
import { VERSAO_ATUAL } from './versoes.js';

const app = document.querySelector<HTMLElement>('#app')!;

// Captura o que ninguém tratou, desde o primeiro instante — inclusive erro que
// aconteça antes do login.
instalarCapturaDeErros();

// As duas redes das fotos, UMA vez cada, para o documento inteiro: foto que
// falhar é rebuscada pelo canal autenticado, e qualquer `<img data-grande>`
// abre a janela de ampliar. Aqui — e não tela por tela — porque instalar por
// tela repetia o ouvinte a cada visita: era o bug do "fechar três vezes".
instalarSocorroDeFotos();
instalarFotoGrande(document.body);

// O service worker do push — registrado sempre; a INSCRIÇÃO só acontece
// quando a pessoa toca em "ativar avisos" (pedir permissão sem gesto é
// como se ganha um "bloquear" para sempre).
void registrarServiceWorker();

// Toda tabela ordena pelo clique no cabeçalho, como no Windows Explorer.
instalarOrdenacaoDeTabelas();

// O convite de instalar o app — some para sempre depois de instalado.
instalarConvitePwa();

// Aba antiga aberta não fica para trás: o detector avisa quando há versão nova.
instalarDetectorDeVersao();

/**
 * O SANDBOX se apresenta como sandbox (pedido do Lucas, 10/08): título da aba
 * gritando, tarja amarela em TODAS as páginas, e o botão que joga fora o que
 * está no banco de teste e o recria como cópia da produção.
 */
const ehSandbox =
  location.hostname === 'sistema-cd-teste.web.app' || location.hostname === 'localhost';
if (ehSandbox) {
  document.title = 'SANDBOX — Sistema CD';
  const tarja = document.createElement('div');
  tarja.className = 'tarja-sandbox';
  tarja.innerHTML = `
    <span>⚠️ <b>SANDBOX</b> — ambiente de testes. Nada aqui mexe no sistema de verdade.</span>
    <button id="sb-clonar" type="button">🔄 Atualizar banco (copiar da produção)</button>
    <button id="sb-montar" type="button">🧪 Montar sandbox (produção + mudanças abertas)</button>
    <button id="sb-promover" type="button">🚀 Escolher o que vai para a produção</button>`;
  document.body.prepend(tarja);
  tarja.querySelector('#sb-clonar')!.addEventListener('click', () => {
    if (
      !confirm(
        'Atualizar o banco do SANDBOX?\n\nTUDO que foi feito aqui nos testes será perdido, e o banco vira uma cópia exata da produção de agora.',
      )
    ) {
      return;
    }
    const botao = tarja.querySelector<HTMLButtonElement>('#sb-clonar')!;
    botao.disabled = true;
    botao.textContent = '⏳ Clonando…';
    void api
      .post<{ linhas: number }>('/api/sandbox/clonar', {})
      .then((r) => {
        botao.textContent = `✓ Clonado (${r.linhas.toLocaleString('pt-BR')} linhas) — recarregando…`;
        setTimeout(() => location.reload(), 1200);
      })
      .catch((e) => {
        botao.disabled = false;
        botao.textContent = '🔄 Atualizar banco (copiar da produção)';
        alert(e instanceof ErroApi ? e.message : 'A clonagem falhou — tente de novo.');
      });
  });

  /**
   * MONTAR O SANDBOX (15/09): o ramo teste vira main + PRs abertos. É o que
   * se aperta depois de abrir ou atualizar uma mudança, para vê-la aqui.
   */
  tarja.querySelector('#sb-montar')!.addEventListener('click', () => {
    if (!confirm('Montar o sandbox de novo?\n\nO ramo de teste vira: produção + todas as mudanças abertas. O robô republica este site em alguns minutos.')) {
      return;
    }
    const botao = tarja.querySelector<HTMLButtonElement>('#sb-montar')!;
    botao.disabled = true;
    botao.textContent = '⏳ Montando…';
    void api
      .post<{ base: string; incluidos: { numero: number; titulo: string }[]; conflitos: { numero: number; titulo: string; erro: string }[] }>('/api/sandbox/montar', {})
      .then((r) => {
        const resumo = `Base: produção ${r.base}\nIncluídas: ${r.incluidos.map((i) => `#${i.numero} ${i.titulo}`).join('\n') || 'nenhuma'}` +
          (r.conflitos.length ? `\n\nFICARAM DE FORA (conflito):\n${r.conflitos.map((c) => `#${c.numero} ${c.titulo}`).join('\n')}` : '');
        alert(`Sandbox montado. O robô está publicando (~4 min).\n\n${resumo}`);
        botao.textContent = '🧪 Montar sandbox (produção + mudanças abertas)';
        botao.disabled = false;
      })
      .catch((e) => {
        botao.disabled = false;
        botao.textContent = '🧪 Montar sandbox (produção + mudanças abertas)';
        alert(e instanceof ErroApi ? e.message : 'Não consegui montar o sandbox — tente de novo.');
      });
  });

  /**
   * APROVAR MUDANÇAS (15/09, Lucas): o botão abre a lista de PRs abertos com
   * uma caixa em cada um. Só os marcados vão para a produção; o resto
   * continua no sandbox. Substitui o "main := teste" de 11/08, que levava
   * tudo junto.
   */
  tarja.querySelector('#sb-promover')!.addEventListener('click', () => {
    document.querySelector('.promo-pagina')?.remove();
    const pagina = document.createElement('div');
    pagina.className = 'promo-pagina';
    pagina.innerHTML = `
      <div class="promo-cartao">
        <h2>🚀 Escolher o que vai para a PRODUÇÃO</h2>
        <div id="promo-corpo"><p class="carregando">Montando o briefing…</p></div>
        <div class="conf-acoes" id="promo-acoes">
          <button class="btn btn-claro" id="promo-fechar" type="button">Fechar</button>
        </div>
      </div>`;
    document.body.appendChild(pagina);
    const corpo = pagina.querySelector<HTMLElement>('#promo-corpo')!;
    const acoes = pagina.querySelector<HTMLElement>('#promo-acoes')!;
    pagina.querySelector('#promo-fechar')!.addEventListener('click', () => pagina.remove());

    interface Pr {
      numero: number;
      titulo: string;
      url: string;
      ramo: string;
      shaCurto: string;
      autor: string;
      quando: string;
      rascunho: boolean;
      noSandbox: boolean;
      mesclavel: boolean | null;
      estado: string;
      commits: { sha: string; titulo: string; quando: string | null }[];
    }
    interface Briefing {
      producao: string;
      main: string;
      teste: string;
      sandbox: string;
      sandboxDesatualizado: boolean;
      prs: Pr[];
    }
    const dataBr = (iso: string | null) =>
      iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';

    void api
      .get<Briefing>('/api/sandbox/briefing')
      .then((b) => {
        if (!b.prs.length) {
          corpo.innerHTML = `<p class="dica">Não há mudanças abertas. Produção em <code>${escapar(b.producao)}</code>.</p>`;
          return;
        }
        const situacao = (p: Pr) => {
          if (p.rascunho) return '<span class="sub-inline">rascunho — não entra</span>';
          if (p.mesclavel === false || p.estado === 'dirty') return '<span class="aviso-inline">⛔ conflito com a produção — precisa de ajuste no ramo</span>';
          if (!p.noSandbox) return '<span class="aviso-inline">⚠️ ainda não está neste sandbox — monte o sandbox e teste antes</span>';
          return '<span class="ok-inline">✓ testável aqui</span>';
        };
        corpo.innerHTML = `
          <p>Produção hoje: <code>${escapar(b.producao)}</code> · sandbox no ar: <code>${escapar(b.sandbox)}</code>
          ${b.sandboxDesatualizado ? ' <span class="aviso-inline">(o robô ainda está publicando o ramo de teste)</span>' : ''}</p>
          <p class="dica">Marque só o que você testou e aprovou. O que ficar desmarcado continua no sandbox.</p>
          <ul class="promo-prs">${b.prs
            .map(
              (p) => `<li>
                <label><input type="checkbox" data-pr="${p.numero}" ${p.noSandbox && p.mesclavel !== false && !p.rascunho ? 'checked' : ''} ${p.rascunho ? 'disabled' : ''}>
                  <b>#${p.numero}</b> ${escapar(p.titulo)}</label>
                <div class="sub-inline"><code>${escapar(p.ramo)}</code> · ${escapar(p.autor)} · ${escapar(dataBr(p.quando))} · ${situacao(p)}
                  · <a href="${escapar(p.url)}" target="_blank" rel="noopener">ver no GitHub</a></div>
                <details><summary>${p.commits.length} commit(s)</summary>
                  <ul class="promo-commits">${p.commits.map((c) => `<li><code>${escapar(c.sha)}</code> ${escapar(c.titulo)} <span class="sub-inline">${escapar(dataBr(c.quando))}</span></li>`).join('')}</ul>
                </details>
              </li>`,
            )
            .join('')}</ul>
          <h3>O aviso que os usuários vão ver <span class="sub-inline">${escapar(VERSAO_ATUAL.versao)}</span></h3>
          <ul class="promo-novidades">${VERSAO_ATUAL.itens.map((i) => `<li>${escapar(i)}</li>`).join('')}</ul>`;
        const enviar = document.createElement('button');
        enviar.className = 'btn btn-grande';
        enviar.textContent = '✓ Enviar as marcadas para a produção';
        acoes.prepend(enviar);
        enviar.addEventListener('click', () => {
          const marcados = [...corpo.querySelectorAll<HTMLInputElement>('input[data-pr]:checked')].map((i) => Number(i.dataset['pr']));
          if (!marcados.length) {
            alert('Marque pelo menos uma mudança.');
            return;
          }
          if (!confirm(`Enviar AGORA para a produção: ${marcados.map((n) => `#${n}`).join(', ')}?\n\nTodos os usuários recebem em alguns minutos. O que não foi marcado fica no sandbox.`)) {
            return;
          }
          enviar.disabled = true;
          enviar.textContent = '⏳ Enviando…';
          void api
            .post<{ mesclados: { numero: number; sha: string }[]; falhas: { numero: number; erro: string }[]; catalogoAtualizado: boolean; alvo: string }>(
              '/api/sandbox/promover',
              { prs: marcados },
            )
            .then((r) => {
              enviar.remove();
              corpo.insertAdjacentHTML(
                'beforeend',
                `<p class="promo-status" id="promo-status">🚚 Mescladas: ${r.mesclados.map((m) => `#${m.numero}`).join(', ')}.
                 ${r.falhas.length ? `<br>⛔ Não foram: ${r.falhas.map((f) => `#${f.numero} (${escapar(f.erro)})`).join('; ')}.` : ''}
                 ${r.catalogoAtualizado ? '<br>📒 Catálogo de versões marcado como produção.' : ''}
                 <br>O robô está construindo e publicando (~4 min)…</p>`,
              );
              const status = corpo.querySelector<HTMLElement>('#promo-status')!;
              const inicio = Date.now();
              const vigiar = () => {
                if (!pagina.isConnected) return;
                void api
                  .get<{ commit: string }>('/api/sandbox/producao-status')
                  .then((s) => {
                    // O catálogo faz um segundo commit na main; qualquer commit
                    // novo depois do envio significa que a produção virou.
                    if (s.commit !== b.producao) {
                      status.insertAdjacentHTML('beforeend', `<br>✅ <b>Produção atualizada e no ar</b> (<code>${escapar(s.commit)}</code>).`);
                      return;
                    }
                    if (Date.now() - inicio > 15 * 60_000) {
                      status.insertAdjacentHTML('beforeend', '<br>⚠️ Está demorando mais que o normal — confira o Cloud Build no console do Google.');
                      return;
                    }
                    setTimeout(vigiar, 20_000);
                  })
                  .catch(() => setTimeout(vigiar, 20_000));
              };
              setTimeout(vigiar, 30_000);
            })
            .catch((e) => {
              enviar.disabled = false;
              enviar.textContent = '✓ Enviar as marcadas para a produção';
              alert(e instanceof ErroApi ? e.message : 'Não consegui enviar — tente de novo.');
            });
        });
      })
      .catch((e) => {
        corpo.innerHTML = `<p class="aviso">${escapar(
          e instanceof ErroApi
            ? e.status === 401 || e.status === 403
              ? 'Entre no sistema como administrador para aprovar a atualização.'
              : e.message
            : 'Não consegui montar o briefing.',
        )}</p>`;
      });
  });
}

interface Eu {
  nome: string;
  email: string | null;
  modulos: string[];
  /** Usuário master: marca própria, separada do módulo Administração. */
  master?: boolean;
}

let eu: Eu | undefined;

onAuthStateChanged(auth, (usuario) => {
  if (usuario) void entrar(usuario);
  else {
    eu = undefined;
    mostrarLogin();
  }
});

window.addEventListener('hashchange', () => {
  if (eu) rotear();
});

// ------------------------------------------------------------------- login

function mostrarLogin(mensagem?: string): void {
  app.innerHTML = `
    <div class="login">
      <div class="login-caixa">
        <img class="login-marca" src="/marca/logo-azul.png" alt="Ademaldo Construções e Projetos">
        <h1>Sistema CD</h1>
        <p>Controle do centro de distribuição</p>
        ${mensagem ? `<div class="aviso">${escapar(mensagem)}</div>` : ''}
        <button class="btn btn-bloco" id="entrar">Entrar com Google</button>
      </div>
    </div>`;

  const botao = app.querySelector<HTMLButtonElement>('#entrar')!;
  botao.addEventListener('click', async () => {
    botao.disabled = true;
    botao.textContent = 'Abrindo o Google…';
    try {
      await signInWithPopup(auth, provedorGoogle);
    } catch (erro) {
      const codigo = (erro as { code?: string }).code ?? '';
      mostrarLogin(
        codigo === 'auth/popup-closed-by-user'
          ? 'Você fechou a janela do Google antes de terminar.'
          : codigo === 'auth/popup-blocked'
            ? 'O navegador bloqueou a janela do Google. Libere o bloqueio de pop-up e tente de novo.'
            : 'Não consegui falar com o Google. Tente de novo.',
      );
    }
  });
}

// ------------------------------------------------------------------ sistema

async function entrar(usuario: User): Promise<void> {
  desenharCasca(usuario.email ?? '');
  const conteudo = app.querySelector<HTMLElement>('#conteudo')!;

  // Abre o cookie que faz as FOTOS carregarem. `<img src>` não manda cabeçalho
  // de autorização — sem este cookie, toda foto do sistema volta 401. Ver
  // `backend/src/auth/sessao.ts`.
  //
  // Falhar aqui não impede de usar o sistema: só as imagens ficam de fora, e é
  // melhor entrar com essa limitação do que barrar a pessoa na porta.
  try {
    await api.post('/api/sessao', {});
  } catch {
    console.warn('sessão de fotos não abriu — as imagens podem não aparecer');
  }

  // Login prova identidade; quem decide o acesso é o cadastro de pessoas. Por
  // isso a primeira coisa é perguntar ao backend — e a recusa vem com o motivo.
  try {
    eu = await api.get<Eu>('/api/eu');
  } catch (erro) {
    if (erro instanceof ErroApi && (erro.status === 401 || erro.status === 403)) {
      conteudo.innerHTML = `
        <div class="centro-estreito">
          <div class="aviso">${escapar(erro.message)}</div>
          <button class="btn btn-claro" id="trocar">Entrar com outra conta</button>
        </div>`;
      conteudo.querySelector('#trocar')!.addEventListener('click', () => void sair());
      return;
    }
    conteudo.innerHTML = `<div class="aviso">${escapar(
      erro instanceof Error ? erro.message : 'Não consegui falar com o servidor.',
    )}</div>`;
    return;
  }

  app.querySelector('#quem')!.innerHTML = `<b>${escapar(eu.nome)}</b>`;
  rotear();
}

/**
 * Sair de verdade: encerra no Firebase E apaga o cookie das fotos.
 *
 * Só `signOut` deixaria o cookie no navegador até vencer sozinho — dias depois.
 * A ordem importa: apaga o cookie primeiro, porque essa chamada precisa chegar
 * antes de a tela trocar para o login.
 */
async function sair(): Promise<void> {
  try {
    await api.del('/api/sessao');
  } catch {
    // Sair não pode falhar por causa da rede.
  }
  await signOut(auth);
}

function desenharCasca(email: string): void {
  app.innerHTML = `
    <header class="topo">
      <div class="faixa">
        <a href="#/" id="ir-hub" title="Voltar ao início">
          <img class="topo-marca" src="/marca/logo-branca.png" alt="Ademaldo">
        </a>
        <span class="rotulo topo-titulo" id="titulo">Sistema CD</span>
        <div class="topo-quem" id="topo-quem">
          <span id="quem">${escapar(email)}</span>
          <button class="btn btn-claro" id="sair">Sair</button>
        </div>
      </div>
    </header>
    <main id="conteudo"><p class="carregando">Verificando seu acesso…</p></main>`;

  app.querySelector('#sair')!.addEventListener('click', () => void sair());
  // O sino dos avisos, em TODA tela: era o que faltava para quem trabalha no
  // Escritório conseguir ativar (14/08).
  instalarSino(app.querySelector<HTMLElement>('#topo-quem')!);
}

function rotear(): void {
  if (!eu) return;
  const conteudo = app.querySelector<HTMLElement>('#conteudo')!;
  const titulo = app.querySelector<HTMLElement>('#titulo')!;
  const chave = location.hash.replace(/^#\/?/, '').split('/')[0] ?? '';

  const m = chave ? modulo(chave) : undefined;
  const permitido = m && eu.modulos.includes(m.chave);

  if (!chave || !m) {
    titulo.textContent = 'Sistema CD';
    montarHub(conteudo, eu.modulos, (c) => {
      location.hash = `#/${c}`;
    });
    return;
  }

  if (!permitido) {
    titulo.textContent = 'Sistema CD';
    conteudo.innerHTML = `
      <div class="centro-estreito">
        <div class="aviso">Você não tem acesso ao módulo “${escapar(m.nome)}”.</div>
        <a class="btn btn-claro" href="#/">Voltar ao início</a>
      </div>`;
    return;
  }

  titulo.textContent = `${m.emoji} ${m.nome}`;
  // O erro capturado passa a saber de qual módulo veio.
  moduloEmUso(m.chave);

  // Só mostra o "voltar" para quem tem mais de um módulo — para quem tem um só,
  // o hub não existe e o botão levaria a lugar nenhum.
  const varios = modulosDe(eu.modulos).length > 1;
  const voltar = varios
    ? `<a class="voltar" href="#/">← Todos os módulos</a>`
    : '';

  conteudo.innerHTML = `${voltar}<div id="modulo"></div>`;
  const alvo = conteudo.querySelector<HTMLElement>('#modulo')!;

  if (m.chave === 'desk') {
    montarDesk(alvo);
  } else if (m.chave === 'entrada') {
    montarEntrada(alvo);
  } else if (m.chave === 'motorista') {
    montarMotorista(alvo);
  } else if (m.chave === 'pedidos') {
    montarPedidos(alvo);
  } else if (m.chave === 'master') {
    montarMaster(alvo);
  } else if (m.chave === 'sst') {
    montarSst(alvo);
  } else {
    alvo.innerHTML = `<div class="vazio">
        <p><b>${m.emoji} ${escapar(m.nome)}</b> ainda não foi migrado.</p>
        <p style="color:var(--cinza);font-size:.9rem">${escapar(m.falta ?? '')}</p>
      </div>`;
  }
}
